import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { useSiteInfo } from "@/lib/site";

const Shell = ({ title, path, description, updated, children }) => (
  <>
    <Seo title={title} path={path} description={description} />
    <div className="px-4 py-8 md:px-6">
      <div className="mx-auto max-w-3xl">
        <h1 className="font-heading text-3xl font-bold text-stone-900 md:text-4xl">{title}</h1>
        <p className="mt-2 text-xs text-stone-400">Last updated: {updated}</p>
        <div className="mt-6 space-y-5 text-[15px] leading-relaxed text-stone-700">{children}</div>
        <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
          This page contains standard placeholder wording. Avinya Bookings’ registered legal entity, GST number and
          registered address should be reviewed and inserted by the business before public launch.
        </div>
      </div>
    </div>
    <Footer />
  </>
);

const H = ({ children }) => <h2 className="font-heading text-lg font-semibold text-stone-900">{children}</h2>;

const useContact = () => {
  const info = useSiteInfo();
  return {
    email: info.support_email || "avinyabookings14@gmail.com",
    phone: info.support_phone || "",
    wa: info.support_whatsapp || "",
  };
};

const UPDATED = "June 2026";

export function Terms() {
  const c = useContact();
  return (
    <Shell title="Terms & Conditions" path="/terms" updated={UPDATED} description="Terms and conditions for using Avinya Bookings to discover and book farmhouses, resorts, banquet halls and function halls around Hyderabad.">
      <p>Welcome to Avinya Bookings. By accessing or using our platform, you agree to these Terms & Conditions. Please read them carefully.</p>
      <H>1. About us</H>
      <p>Avinya Bookings is a booking marketplace that connects customers with owners of farmhouses, resorts, banquet halls and function halls, primarily in and around Hyderabad, India.</p>
      <H>2. Bookings</H>
      <p>A booking request is confirmed only after the property owner accepts it and the applicable advance payment is successfully verified by our backend. Prices, advance amounts, taxes and any fees are calculated and displayed before you pay.</p>
      <H>3. Payments</H>
      <p>Payments are processed securely through our payment partner (Razorpay). Avinya Bookings does not store your card, CVV or banking credentials. A platform commission may apply to confirmed bookings.</p>
      <H>4. Cancellations & refunds</H>
      <p>Cancellations and refunds are governed by our <a className="text-primary underline" href="/cancellation-policy">Cancellation Policy</a> and <a className="text-primary underline" href="/refund-policy">Refund Policy</a>, shown before you pay.</p>
      <H>5. User responsibilities</H>
      <p>You agree to provide accurate information, use the platform lawfully, and respect property rules set by owners. Owners are responsible for the accuracy of their listings and for honouring confirmed bookings.</p>
      <H>6. Limitation of liability</H>
      <p>Avinya Bookings acts as an intermediary. To the extent permitted by law, we are not liable for disputes between customers and owners, or for the condition of any property.</p>
      <H>7. Contact</H>
      <p>Questions? Email {c.email}{c.wa ? ` or message us on WhatsApp.` : "."}</p>
    </Shell>
  );
}

export function Privacy() {
  const c = useContact();
  return (
    <Shell title="Privacy Policy" path="/privacy" updated={UPDATED} description="How Avinya Bookings collects, uses and protects your personal information.">
      <p>This Privacy Policy explains what information Avinya Bookings collects and how we use it. We collect only what we need to provide our booking service.</p>
      <H>1. Information we collect</H>
      <p>Account details (name, email, phone), booking details, and limited marketing attribution (e.g. which campaign referred you). We do not store card numbers, CVV or banking credentials — payments are handled by our payment partner.</p>
      <H>2. How we use information</H>
      <p>To create and manage bookings, process payments, send transactional updates, provide support, and — only with your consent — send marketing communications.</p>
      <H>3. Cookies & analytics</H>
      <p>We use essential cookies to run the platform and optional analytics to improve it. You can decline analytics via our cookie notice at any time.</p>
      <H>4. Marketing consent</H>
      <p>Promotional messages (including WhatsApp) are sent only where you have consented. You may withdraw consent at any time by contacting support.</p>
      <H>5. Data sharing</H>
      <p>We share booking details with the relevant property owner to fulfil your booking, and with our payment partner to process payments. We do not sell your personal data.</p>
      <H>6. Your rights</H>
      <p>You may request access to, correction of, or deletion of your personal data by contacting us at {c.email}.</p>
      <H>7. Contact</H>
      <p>For privacy queries, email {c.email}.</p>
    </Shell>
  );
}

export function CancellationPolicy() {
  return (
    <Shell title="Cancellation Policy" path="/cancellation-policy" updated={UPDATED} description="Avinya Bookings cancellation policy for farmhouse, resort, banquet hall and function hall bookings.">
      <p>The following cancellation windows apply to confirmed bookings. Exact values are configured by Avinya and always shown to you before payment.</p>
      <H>Cancellation windows (default)</H>
      <ul className="list-disc space-y-2 pl-5">
        <li><strong>7 or more days</strong> before the booking date: eligible for a <strong>full refund</strong> of the advance paid.</li>
        <li><strong>3 to 6 days</strong> before: eligible for a <strong>partial refund</strong> (typically 50% of the advance).</li>
        <li><strong>Within 72 hours</strong> of the booking date: <strong>no refund</strong>.</li>
      </ul>
      <H>How to cancel</H>
      <p>Open your booking under “My Bookings” and choose Cancel, or contact support. The refund amount, if any, is calculated automatically according to the policy in effect at the time of your booking.</p>
      <H>Owner-initiated cancellations</H>
      <p>If an owner cancels a confirmed booking, you are entitled to a full refund of the advance paid.</p>
    </Shell>
  );
}

export function RefundPolicy() {
  const c = useContact();
  return (
    <Shell title="Refund Policy" path="/refund-policy" updated={UPDATED} description="How refunds are calculated and processed on Avinya Bookings.">
      <p>Refunds are processed in line with our <a className="text-primary underline" href="/cancellation-policy">Cancellation Policy</a>. This page explains how and when refunds are issued.</p>
      <H>1. Eligibility</H>
      <p>Refund eligibility and amount depend on how far in advance you cancel, as set out in the Cancellation Policy shown before payment.</p>
      <H>2. Refund method</H>
      <p>Eligible refunds are issued to your original payment method via our payment partner (Razorpay). Avinya Bookings never handles or stores your card or banking details.</p>
      <H>3. Processing time</H>
      <p>Once approved, refunds are typically initiated immediately and may take 5–7 business days to reflect, depending on your bank or card issuer.</p>
      <H>4. Disputes</H>
      <p>If you believe a refund is incorrect, contact us at {c.email} with your booking reference and we will review it.</p>
    </Shell>
  );
}
